import { useEffect, useRef } from 'react';

import { SECRET_SHOWN_MS } from '@/lib/secretMask';

/** Sent by the native shell before the screen is covered. */
export const HIDE_EVENT = 'bridge:hide';

/**
 * Hides a shown secret again when the page is hidden (another tab, the app going to the background, `pagehide`) and
 * after `ms` on the screen. `hide` may change on every render; the clock starts when `shown` becomes true.
 */
export function useAutoHide(shown: boolean, hide: () => void, ms: number = SECRET_SHOWN_MS): void {
  const latest = useRef(hide);
  useEffect(() => {
    latest.current = hide;
  });

  useEffect(() => {
    if (!shown) return;
    const hideNow = (): void => {
      latest.current();
    };
    const onVisibility = (): void => {
      if (document.visibilityState === 'hidden') hideNow();
    };
    const timer = window.setTimeout(hideNow, ms);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', hideNow);
    // the app's shell says the screen is about to be seen by the app switcher or a recording (apps/apple/Bridge/PrivacyCover.swift)
    window.addEventListener(HIDE_EVENT, hideNow);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', hideNow);
      window.removeEventListener(HIDE_EVENT, hideNow);
    };
  }, [shown, ms]);
}
